import { Waves, Sparkles, Utensils, Sun, Leaf, ConciergeBell, Heart, Dumbbell, Briefcase, GlassWater, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { AMENITIES, OFFICIAL_BOOKING_URL } from "@/lib/data";
import SectionTitle from "@/components/SectionTitle";
import RotatingVideo from "@/components/RotatingVideo";
import { AMENITIES_VIDEOS } from "@/lib/videos";

const iconMap = { waves: Waves, sparkles: Sparkles, utensils: Utensils, sun: Sun, leaf: Leaf, "concierge-bell": ConciergeBell, heart: Heart, dumbbell: Dumbbell, briefcase: Briefcase, "glass-water": GlassWater };

export default function Amenities() {
    return (
        <div data-testid="amenities-page">
            <RotatingVideo
                videos={AMENITIES_VIDEOS}
                eager
                testId="amenities-page-video"
                className="relative w-full h-[92vh] min-h-[640px]"
                overlay={
                    <>
                        <div className="absolute inset-0 bg-gradient-to-t from-navy/75 via-navy/20 to-navy/40" />
                        <div className="absolute inset-0 max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-end pb-16 md:pb-24">
                            <div className="text-[11px] uppercase tracking-[0.28em] text-gold mb-4 flex items-center gap-3"><span className="w-10 h-[1px] bg-gold" /> Amenities</div>
                            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl text-ivory max-w-3xl leading-[1.02]">Considered <span className="font-serif-italic text-gold">to the last detail</span>.</h1>
                            <p className="mt-6 text-ivory/85 max-w-xl leading-relaxed">Everything at Serenity Cove is designed for rest — from spa rituals to the way the light falls across the pool at four in the afternoon.</p>
                            <div className="mt-8 flex items-center gap-3">
                                <a href="#amenities-list" className="btn-gold px-6 py-3 rounded-full">Explore Amenities</a>
                                <Link to="/booking" className="btn-outline-ivory px-6 py-3 rounded-full">Book Your Stay</Link>
                            </div>
                        </div>
                    </>
                }
            />
            <div id="amenities-list" className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-32 space-y-16">
                {AMENITIES.map((a, i) => {
                    const Icon = iconMap[a.icon] || Waves;
                    return (
                        <article key={a.key} data-testid={`amenity-row-${a.key}`} className={`grid md:grid-cols-2 gap-10 items-center ${i % 2 === 1 ? "md:[&>div:first-child]:order-2" : ""}`}>
                            <div className="rounded-2xl overflow-hidden aspect-[4/3] relative group">
                                <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
                                {a.images && a.images.length > 1 && a.images.slice(1).map((extra, idx) => (
                                    <img
                                        key={idx}
                                        src={extra}
                                        alt={`${a.title} — view ${idx + 2}`}
                                        className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                                        style={{ transitionDelay: `${idx * 900}ms` }}
                                    />
                                ))}
                                {a.video && (
                                    <video
                                        src={a.video}
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        preload="metadata"
                                        className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                                        style={{ transitionDelay: `${((a.images ? a.images.length : 1)) * 900}ms` }}
                                    />
                                )}
                                {a.video && (
                                    <span className="absolute bottom-3 right-3 bg-navy/70 text-ivory text-[10px] uppercase tracking-[0.24em] px-3 py-1.5 rounded-full backdrop-blur-sm flex items-center gap-1.5">
                                        <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" /> Video
                                    </span>
                                )}
                            </div>
                            <div>
                                <div className="w-12 h-12 rounded-full bg-sand/60 text-bronze flex items-center justify-center"><Icon size={20} /></div>
                                <h2 className="font-display text-4xl md:text-5xl text-navy mt-6">{a.title}</h2>
                                <p className="mt-4 text-navy/70 leading-relaxed">{a.body}</p>
                                <p className="mt-4 font-serif-italic text-jade text-lg">Open daily · Reservations via concierge.</p>
                                <div className="mt-6 flex items-center gap-3">
                                    <Link to="/booking" data-testid={`amenity-page-explore-${a.key}`} className="btn-gold px-5 py-2.5 rounded-full inline-flex items-center gap-2">Explore Amenity <ArrowRight size={12} /></Link>
                                    <Link to="/contact" data-testid={`amenity-page-learn-${a.key}`} className="border border-navy text-navy px-5 py-2.5 rounded-full text-[11px] uppercase tracking-[0.16em] hover:bg-navy hover:text-ivory transition-colors">Learn More</Link>
                                </div>
                            </div>
                        </article>
                    );
                })}
            </div>
        </div>
    );
}
